/**
 * Privacy Policy page for Vibe Commenting
 */

import React from 'react';
import { Link } from 'react-router-dom';
import AuroraBackground from '../components/AuroraBackground';
import SEO from '../components/SEO';

const PrivacyPolicyPage: React.FC = () => {
    const lastUpdated = 'March 27, 2026';

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const sections = [
        { id: 'introduction', title: '1. Introduction' },
        { id: 'information-we-collect', title: '2. Information We Collect' },
        { id: 'how-we-use', title: '3. How We Use Your Information' },
        { id: 'data-storage', title: '4. Data Storage & Security' },
        { id: 'third-party', title: '5. Third-Party Services' },
        { id: 'data-retention', title: '6. Data Retention & Deletion' },
        { id: 'your-rights', title: '7. Your Rights (GDPR)' },
        { id: 'children', title: '8. Children\'s Privacy' },
        { id: 'international', title: '9. International Data Transfers' },
        { id: 'changes', title: '10. Changes to This Policy' },
        { id: 'contact', title: '11. Contact Us' },
    ];

    return (
        <>
            <SEO
                title="Privacy Policy | Vibe Commenting"
                description="Read Vibe Commenting's privacy policy. Learn how we handle your data, Instagram account info, and AI-generated comments."
                path="/privacy"
            />
            <AuroraBackground />
            <div style={styles.container}>
                <div style={styles.card}>
                    {/* Header */}
                    <div style={styles.header}>
                        <h1 style={styles.title}>Privacy Policy</h1>
                        <p style={styles.lastUpdated}>Last updated: {lastUpdated}</p>
                    </div>

                    {/* Table of Contents */}
                    <nav style={styles.toc}>
                        <h2 style={styles.tocTitle}>Table of Contents</h2>
                        <ul style={styles.tocList}>
                            {sections.map((section) => (
                                <li key={section.id} style={styles.tocItem}>
                                    <button
                                        onClick={() => scrollToSection(section.id)}
                                        style={styles.tocLink}
                                    >
                                        {section.title}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    {/* Policy Content */}
                    <div style={styles.content}>
                        {/* 1. Introduction */}
                        <section id="introduction" style={styles.section}>
                            <h2 style={styles.sectionTitle}>1. Introduction</h2>
                            <p style={styles.paragraph}>
                                Welcome to Vibe Commenting. This Privacy Policy explains how we collect, use,
                                disclose, and safeguard your information when you use our AI-powered Instagram
                                engagement service. Vibe Commenting helps users build authentic Instagram
                                connections by generating personalized comments using artificial intelligence.
                            </p>
                            <p style={styles.paragraph}>
                                By using our service, you agree to the collection and use of information in
                                accordance with this policy. If you do not agree with the terms of this privacy
                                policy, please do not access the service.
                            </p>
                        </section>

                        {/* 2. Information We Collect */}
                        <section id="information-we-collect" style={styles.section}>
                            <h2 style={styles.sectionTitle}>2. Information We Collect</h2>

                            <h3 style={styles.subheading}>Google Account Data</h3>
                            <p style={styles.paragraph}>
                                When you sign in using Google authentication, we collect:
                            </p>
                            <ul style={styles.list}>
                                <li>Email address</li>
                                <li>Display name</li>
                                <li>Profile picture URL</li>
                                <li>Google account unique identifier</li>
                            </ul>

                            <h3 style={styles.subheading}>Instagram Account Details</h3>
                            <p style={styles.paragraph}>
                                To provide our comment posting service, we collect:
                            </p>
                            <ul style={styles.list}>
                                <li>Instagram username</li>
                                <li>Instagram account credentials (securely encrypted for automated posting)</li>
                                <li>Profile information from your public Instagram account</li>
                            </ul>

                            <h3 style={styles.subheading}>Usage Data</h3>
                            <p style={styles.paragraph}>
                                We automatically collect certain information when you use our service:
                            </p>
                            <ul style={styles.list}>
                                <li>Session information and activity logs</li>
                                <li>Comments generated and their approval/rejection status</li>
                                <li>Service preferences and settings</li>
                                <li>Device information and browser type</li>
                                <li>IP address and approximate location</li>
                            </ul>

                            <h3 style={styles.subheading}>Payment Data</h3>
                            <p style={styles.paragraph}>
                                Payment processing is handled by Stripe. We do not store your credit card
                                details directly. Stripe may collect and store:
                            </p>
                            <ul style={styles.list}>
                                <li>Billing name and address</li>
                                <li>Payment method details (stored securely by Stripe)</li>
                                <li>Transaction history</li>
                            </ul>
                        </section>

                        {/* 3. How We Use Your Information */}
                        <section id="how-we-use" style={styles.section}>
                            <h2 style={styles.sectionTitle}>3. How We Use Your Information</h2>
                            <p style={styles.paragraph}>
                                We use the collected information for the following purposes:
                            </p>
                            <ul style={styles.list}>
                                <li><strong>Service Delivery:</strong> To generate AI-powered comments and post them to Instagram on your behalf</li>
                                <li><strong>Account Management:</strong> To create and manage your user account</li>
                                <li><strong>Payment Processing:</strong> To process credit purchases and manage billing</li>
                                <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our AI algorithms and user experience</li>
                                <li><strong>Communication:</strong> To send you service updates, security alerts, and support messages</li>
                                <li><strong>Compliance:</strong> To comply with legal obligations and enforce our terms of service</li>
                            </ul>
                        </section>

                        {/* 4. Data Storage & Security */}
                        <section id="data-storage" style={styles.section}>
                            <h2 style={styles.sectionTitle}>4. Data Storage & Security</h2>
                            <p style={styles.paragraph}>
                                We take data security seriously and implement industry-standard measures to
                                protect your information:
                            </p>
                            <ul style={styles.list}>
                                <li><strong>Infrastructure:</strong> Your data is stored on Firebase/Google Cloud Platform, which provides enterprise-grade security</li>
                                <li><strong>Encryption in Transit:</strong> All data transmitted between your device and our servers is encrypted using TLS/SSL</li>
                                <li><strong>Encryption at Rest:</strong> Sensitive data, including Instagram credentials, is encrypted at rest</li>
                                <li><strong>Access Controls:</strong> We implement strict access controls and authentication mechanisms</li>
                                <li><strong>Regular Audits:</strong> We regularly review and update our security practices</li>
                            </ul>
                            <p style={styles.paragraph}>
                                While we strive to use commercially acceptable means to protect your personal
                                information, no method of transmission over the internet or electronic storage
                                is 100% secure.
                            </p>
                        </section>

                        {/* 5. Third-Party Services */}
                        <section id="third-party" style={styles.section}>
                            <h2 style={styles.sectionTitle}>5. Third-Party Services</h2>
                            <p style={styles.paragraph}>
                                We use the following third-party services to operate Vibe Commenting:
                            </p>

                            <h3 style={styles.subheading}>Google (Authentication & Cloud)</h3>
                            <p style={styles.paragraph}>
                                We use Google for user authentication (Sign in with Google) and cloud
                                infrastructure (Firebase, Google Cloud Platform). Google's privacy policy
                                can be found at: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>https://policies.google.com/privacy</a>
                            </p>

                            <h3 style={styles.subheading}>Stripe (Payments)</h3>
                            <p style={styles.paragraph}>
                                We use Stripe to process payments securely. Stripe's privacy policy can be
                                found at: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>https://stripe.com/privacy</a>
                            </p>

                            <h3 style={styles.subheading}>Instagram (API for Posting)</h3>
                            <p style={styles.paragraph}>
                                We interact with Instagram to post comments on your behalf. Your use of
                                Instagram is subject to Instagram's terms of service and privacy policy: <a href="https://privacycenter.instagram.com/policy" target="_blank" rel="noopener noreferrer" style={styles.link}>https://privacycenter.instagram.com/policy</a>
                            </p>
                        </section>

                        {/* 6. Data Retention & Deletion */}
                        <section id="data-retention" style={styles.section}>
                            <h2 style={styles.sectionTitle}>6. Data Retention & Deletion</h2>
                            <p style={styles.paragraph}>
                                We retain your personal data only for as long as necessary to provide you
                                with our services and as described in this privacy policy.
                            </p>
                            <p style={styles.paragraph}>
                                <strong>Account Deletion:</strong> When you delete your account, we will
                                delete or anonymize all your personal data, including:
                            </p>
                            <ul style={styles.list}>
                                <li>Your profile information</li>
                                <li>All Instagram accounts you own</li>
                                <li>All sessions and generated comments</li>
                                <li>Purchase and billing data (subject to legal retention requirements)</li>
                            </ul>
                            <p style={styles.paragraph}>
                                <strong>How to Request Deletion:</strong> You can delete your account directly
                                from the user menu in the application. Alternatively, you can contact us at{' '}
                                <a href="mailto:yuyang.jiang@atelier-persopol.com" style={styles.link}>
                                    yuyang.jiang@atelier-persopol.com
                                </a>{' '}
                                to request account deletion.
                            </p>
                        </section>

                        {/* 7. Your Rights (GDPR) */}
                        <section id="your-rights" style={styles.section}>
                            <h2 style={styles.sectionTitle}>7. Your Rights (GDPR)</h2>
                            <p style={styles.paragraph}>
                                If you are a resident of the European Economic Area (EEA), you have certain
                                data protection rights under the General Data Protection Regulation (GDPR):
                            </p>
                            <ul style={styles.list}>
                                <li><strong>Right to Access:</strong> You have the right to request copies of your personal data</li>
                                <li><strong>Right to Rectification:</strong> You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete</li>
                                <li><strong>Right to Erasure:</strong> You have the right to request that we erase your personal data, under certain conditions</li>
                                <li><strong>Right to Restrict Processing:</strong> You have the right to request that we restrict the processing of your personal data, under certain conditions</li>
                                <li><strong>Right to Data Portability:</strong> You have the right to request that we transfer the data we have collected to another organization, or directly to you, under certain conditions</li>
                                <li><strong>Right to Object:</strong> You have the right to object to our processing of your personal data, under certain conditions</li>
                            </ul>
                            <p style={styles.paragraph}>
                                To exercise any of these rights, please contact us at{' '}
                                <a href="mailto:yuyang.jiang@atelier-persopol.com" style={styles.link}>
                                    yuyang.jiang@atelier-persopol.com
                                </a>.
                                We will respond to your request within 30 days.
                            </p>
                        </section>

                        {/* 8. Children's Privacy */}
                        <section id="children" style={styles.section}>
                            <h2 style={styles.sectionTitle}>8. Children's Privacy</h2>
                            <p style={styles.paragraph}>
                                Our service is intended for users who are 18 years of age or older. We do
                                not knowingly collect personal information from children under 18. If you
                                are a parent or guardian and you are aware that your child has provided us
                                with personal data, please contact us. If we become aware that we have
                                collected personal data from children without verification of parental
                                consent, we will take steps to remove that information from our servers.
                            </p>
                        </section>

                        {/* 9. International Data Transfers */}
                        <section id="international" style={styles.section}>
                            <h2 style={styles.sectionTitle}>9. International Data Transfers</h2>
                            <p style={styles.paragraph}>
                                Your information may be transferred to and maintained on computers located
                                outside of your state, province, country, or other governmental jurisdiction
                                where the data protection laws may differ from those of your jurisdiction.
                            </p>
                            <p style={styles.paragraph}>
                                If you are located outside the United States and choose to provide information
                                to us, please note that we transfer the data to the United States and process
                                it there. Your consent to this privacy policy followed by your submission of
                                such information represents your agreement to that transfer.
                            </p>
                            <p style={styles.paragraph}>
                                For EEA residents, we ensure appropriate safeguards are in place for
                                international data transfers, including Standard Contractual Clauses approved
                                by the European Commission.
                            </p>
                        </section>

                        {/* 10. Changes to This Policy */}
                        <section id="changes" style={styles.section}>
                            <h2 style={styles.sectionTitle}>10. Changes to This Policy</h2>
                            <p style={styles.paragraph}>
                                We may update our Privacy Policy from time to time. We will notify you of
                                any changes by posting the new Privacy Policy on this page and updating
                                the "Last updated" date at the top of this policy.
                            </p>
                            <p style={styles.paragraph}>
                                You are advised to review this Privacy Policy periodically for any changes.
                                Changes to this Privacy Policy are effective when they are posted on this page.
                            </p>
                        </section>

                        {/* 11. Contact Us */}
                        <section id="contact" style={styles.section}>
                            <h2 style={styles.sectionTitle}>11. Contact Us</h2>
                            <p style={styles.paragraph}>
                                If you have any questions about this Privacy Policy, please contact us:
                            </p>
                            <div style={styles.contactInfo}>
                                <p style={styles.paragraph}>
                                    <strong>Vibe Commenting</strong><br />
                                    Email: <a href="mailto:yuyang.jiang@atelier-persopol.com" style={styles.link}>
                                        yuyang.jiang@atelier-persopol.com
                                    </a>
                                </p>
                            </div>
                        </section>
                    </div>

                    {/* Back to Home Button */}
                    <div style={styles.footer}>
                        <Link to="/" style={styles.button}>
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '100px 20px 40px',
    },
    card: {
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '800px',
        width: '100%',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    },
    header: {
        textAlign: 'center',
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    },
    title: {
        fontSize: '36px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '8px',
    },
    lastUpdated: {
        fontSize: '14px',
        color: '#666666',
    },
    toc: {
        background: '#F7F7F7',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '32px',
    },
    tocTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#000000',
        marginBottom: '16px',
    },
    tocList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '8px',
    },
    tocItem: {
        margin: 0,
    },
    tocLink: {
        background: 'none',
        border: 'none',
        color: '#7C3AED',
        fontSize: '14px',
        cursor: 'pointer',
        textAlign: 'left',
        padding: '4px 0',
        textDecoration: 'none',
        transition: 'color 0.2s ease',
    },
    content: {
        color: '#333333',
    },
    section: {
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    },
    sectionTitle: {
        fontSize: '22px',
        fontWeight: 600,
        color: '#000000',
        marginBottom: '16px',
        paddingTop: '16px',
    },
    subheading: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#333333',
        marginTop: '20px',
        marginBottom: '12px',
    },
    paragraph: {
        fontSize: '15px',
        lineHeight: 1.7,
        marginBottom: '12px',
        color: '#444444',
    },
    list: {
        paddingLeft: '24px',
        marginBottom: '16px',
        lineHeight: 1.8,
    },
    link: {
        color: '#7C3AED',
        textDecoration: 'none',
    },
    contactInfo: {
        background: '#F7F7F7',
        borderRadius: '8px',
        padding: '20px',
        marginTop: '16px',
    },
    footer: {
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
    },
    button: {
        display: 'inline-block',
        background: '#7C3AED',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '16px 32px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'all 0.2s ease',
    },
};

export default PrivacyPolicyPage;
